import React, { useState } from 'react';
import { ethers } from 'ethers';
import { calculateImpermanentLoss } from '../config/api';

const ILChecker = () => {
  const [txHash, setTxHash] = useState('');
  const [network, setNetwork] = useState('HYPEREVM');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const amountFormatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });

  const formatCurrency = (value) =>
    currencyFormatter.format(value ?? 0).replace(/\u00A0/g, " ");

  const formatAmount = (value) => amountFormatter.format(value ?? 0);

  const formatTokenValue = (price, amount) => {
    const total = (price ?? 0) * (amount ?? 0);
    return {
      price: formatCurrency(price),
      amount: formatAmount(amount),
      value: formatCurrency(total),
    };
  };

  const formatPercent = (value) =>
    `${value >= 0 ? "+" : ""}${(value ?? 0).toFixed(2)}%`;

  const tokenValues = result
    ? {
        token0: formatTokenValue(result.initial.price0, result.initial.amount0),
        token1: formatTokenValue(result.initial.price1, result.initial.amount1),
        totalInitialValue: formatCurrency(result.initial.value),
      }
    : null;

  // RPC URLs pour chaque network
  const RPC_URLS = {
    ETH: 'https://eth.llamarpc.com',
    BSC: 'https://bsc-dataseed.binance.org',
    MATIC: 'https://polygon-rpc.com',
    ARB: 'https://arb1.arbitrum.io/rpc',
    BASE: 'https://mainnet.base.org',
    AVAX: 'https://api.avax.network/ext/bc/C/rpc',
    FTM: 'https://rpc.ftm.tools',
    HYPEREVM: 'https://rpc.hyperliquid.xyz/evm',
  };

  const handleAnalyze = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      // Créer un provider pour le network sélectionné
      const provider = new ethers.JsonRpcProvider(RPC_URLS[network]);
      
      // Appeler ta fonction API
      const ilResult = await calculateImpermanentLoss(network, txHash, provider);
      
      if (!ilResult.success) {
        throw new Error(ilResult.error || 'Erreur lors de l\'analyse');
      }
      
      setResult(ilResult.data);
      
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const diffBadgeClass = (value) =>
    value >= 0
      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
      : "border-rose-500/40 bg-rose-500/10 text-rose-300";

  const priceChanges = result
    ? {
        token0:
          result.initial.price0 !== 0
            ? ((result.current.price0 - result.initial.price0) /
                result.initial.price0) *
              100
            : 0,
        token1:
          result.initial.price1 !== 0
            ? ((result.current.price1 - result.initial.price1) /
                result.initial.price1) *
              100
            : 0,
      }
    : null;

  return (
    <div className="px-4 py-12 min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100">
      <div className="flex flex-col gap-8 mx-auto max-w-5xl">
        <section className="p-6 rounded-3xl border shadow-2xl backdrop-blur border-slate-800 bg-slate-900/60 md:p-10">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4 text-center md:flex-row md:items-center md:justify-between md:text-left">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.4em] text-indigo-400">
                  Outil DeFi
                </p>
                <h2 className="text-3xl font-bold text-white md:text-4xl">
                  🔍 Impermanent Loss Checker
                </h2>
                <p className="mt-2 text-base text-slate-400">
                  Analyse instantanément vos dépôts LP et comparez-les avec une
                  stratégie de hold classique.
                </p>
              </div>
              <div className="px-6 py-4 text-left rounded-2xl border shadow-inner border-indigo-500/40 bg-indigo-500/10">
                <p className="text-xs font-semibold tracking-wide text-indigo-300 uppercase">
                  Valeur LP actuelle
                </p>
                <p className="mt-1 text-2xl font-bold text-white">
                  {formatCurrency(result?.current?.lpValue ?? 0)}
                </p>
                <p className="text-xs text-slate-400">
                  Affiché après une analyse réussie
                </p>
              </div>
            </div>

            <div className="p-5 rounded-2xl border shadow-inner border-slate-800 bg-slate-950/40">
              <div className="grid gap-4 md:grid-cols-[1fr_1fr_auto] md:items-end">
                <div className="grid gap-2">
                  <label
                    htmlFor="network"
                    className="text-xs font-semibold tracking-wide uppercase text-slate-400"
                  >
                    Network
                  </label>
                  <select
                    id="network"
                    value={network}
                    onChange={(e) => setNetwork(e.target.value)}
                    disabled={loading}
                    className="px-4 w-full h-12 text-sm font-medium rounded-2xl border shadow-lg transition border-slate-700 bg-slate-900/70 text-slate-100 shadow-slate-950/40 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <option value="HYPEREVM">HyperEVM</option>
                    <option value="ETH">Ethereum</option>
                    <option value="BSC">BSC</option>
                    <option value="MATIC">Polygon</option>
                    <option value="ARB">Arbitrum</option>
                    <option value="BASE">Base</option>
                    <option value="AVAX">Avalanche</option>
                    <option value="FTM">Fantom</option>
                  </select>
                </div>

                <div className="grid gap-2">
                  <label
                    htmlFor="txHash"
                    className="text-xs font-semibold tracking-wide uppercase text-slate-400"
                  >
                    Transaction Hash
                  </label>
                  <input
                    id="txHash"
                    type="text"
                    placeholder="0x..."
                    value={txHash}
                    onChange={(e) => setTxHash(e.target.value)}
                    disabled={loading}
                    className="px-4 w-full h-12 text-sm font-medium rounded-2xl border shadow-lg transition border-slate-700 bg-slate-900/70 text-slate-100 shadow-slate-950/40 placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 disabled:cursor-not-allowed disabled:opacity-60"
                  />
                </div>

                <button
                  onClick={handleAnalyze}
                  disabled={!txHash || loading}
                  className="px-6 h-12 text-sm font-semibold tracking-wide text-white uppercase bg-gradient-to-r from-indigo-500 via-purple-500 to-blue-500 rounded-2xl shadow-lg transition shadow-indigo-900/40 hover:shadow-indigo-500/50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? "⏳ Analyse..." : "📊 Analyser"}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-4 text-sm text-rose-100 rounded-2xl border shadow-lg border-rose-500/40 bg-rose-500/10 shadow-rose-900/40">
                <div className="flex gap-3 items-start">
                  <span className="text-xl">❌</span>
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-white">Erreur</p>
                    <p className="text-rose-100/80">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {result && tokenValues && (
              <div className="p-6 space-y-6 rounded-3xl border shadow-xl border-slate-800 bg-slate-900/60 md:p-8">
                <div className="flex flex-wrap gap-3 justify-between items-center">
                  <h3 className="text-2xl font-semibold text-white">
                    Résultats
                  </h3>
                  <div className="px-4 py-1 text-xs font-semibold rounded-full border border-slate-700 bg-slate-800/60 text-slate-300">
                    {new Date(result.initial.date).toLocaleDateString("fr-FR", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </div>
                </div>

                <div className="flex flex-col gap-4 p-5 rounded-2xl border shadow-inner border-slate-800 bg-slate-950/40 md:flex-row md:items-center md:justify-between">
                  <div className="flex flex-col flex-1 gap-2">
                    <span className="text-xs font-semibold tracking-wide uppercase text-slate-500">
                      Token A
                    </span>
                    <span className="text-lg font-semibold text-white">
                      {result.tokens.token0}
                    </span>
                    <span className="text-sm text-indigo-200">
                      {`${tokenValues.token0.price} × ${tokenValues.token0.amount} = ${tokenValues.token0.value}`}
                    </span>
                  </div>
                  <div className="hidden w-px h-14 bg-slate-800 md:block" />
                  <div className="flex flex-col flex-1 gap-2">
                    <span className="text-xs font-semibold tracking-wide uppercase text-slate-500">
                      Token B
                    </span>
                    <span className="text-lg font-semibold text-white">
                      {result.tokens.token1}
                    </span>
                    <span className="text-sm text-indigo-200">
                      {`${tokenValues.token1.price} × ${tokenValues.token1.amount} = ${tokenValues.token1.value}`}
                    </span>
                  </div>
                  <div className="px-4 py-1 text-xs font-semibold text-amber-200 rounded-full border border-amber-400/30 bg-amber-400/10">
                    {`Total initial : ${tokenValues.totalInitialValue}`}
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <div className="p-5 bg-gradient-to-br rounded-2xl border shadow-lg border-indigo-500/40 from-indigo-500/20 via-purple-500/10 to-blue-500/10 shadow-indigo-900/40">
                    <div className="text-3xl">💼</div>
                    <p className="mt-2 text-sm font-semibold tracking-wide text-indigo-200 uppercase">
                      Position LP
                    </p>
                    <p className="mt-2 text-3xl font-bold text-white">
                      {formatCurrency(result.current.lpValue)}
                    </p>
                    <div className="mt-3 text-sm text-slate-200">
                      <div>
                        {formatAmount(result.current.amount0)}{" "}
                        <span className="text-slate-400">
                          {result.tokens.token0}
                        </span>
                      </div>
                      <div>
                        {formatAmount(result.current.amount1)}{" "}
                        <span className="text-slate-400">
                          {result.tokens.token1}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-5 rounded-2xl border shadow-lg border-slate-800 bg-slate-900/60 shadow-slate-950/40">
                    <div className="text-2xl">🪙</div>
                    <p className="mt-2 text-sm font-semibold tracking-wide uppercase text-slate-300">
                      Hold {result.tokens.token0}
                    </p>
                    <p className="mt-2 text-3xl font-bold text-white">
                      {formatCurrency(result.current.hold0Value)}
                    </p>
                    <span
                      className={`mt-4 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${diffBadgeClass(
                        result.il.vs0
                      )}`}
                    >
                      {result.il.vs0 >= 0 ? "▲" : "▼"} {formatPercent(result.il.vs0)}
                    </span>
                  </div>

                  <div className="p-5 rounded-2xl border shadow-lg border-slate-800 bg-slate-900/60 shadow-slate-950/40">
                    <div className="text-2xl">🪙</div>
                    <p className="mt-2 text-sm font-semibold tracking-wide uppercase text-slate-300">
                      Hold {result.tokens.token1}
                    </p>
                    <p className="mt-2 text-3xl font-bold text-white">
                      {formatCurrency(result.current.hold1Value)}
                    </p>
                    <span
                      className={`mt-4 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${diffBadgeClass(
                        result.il.vs1
                      )}`}
                    >
                      {result.il.vs1 >= 0 ? "▲" : "▼"} {formatPercent(result.il.vs1)}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col gap-3 px-5 py-4 text-emerald-200 rounded-2xl border shadow-lg border-emerald-500/40 bg-emerald-500/10 shadow-emerald-900/40 md:flex-row md:items-center md:justify-between">
                  <div className="flex gap-3 items-center text-xl font-semibold text-white">
                    <span className="text-2xl">🏆</span>
                    <span>Meilleure stratégie</span>
                  </div>
                  <div className="text-lg font-semibold text-emerald-100">
                    {result.bestStrategy}
                  </div>
                </div>

                <div className="p-5 space-y-4 rounded-2xl border shadow-inner border-slate-800 bg-slate-950/40">
                  <h4 className="text-lg font-semibold text-white">
                    💹 Évolution des prix
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex flex-wrap gap-3 justify-between items-center">
                      <span className="font-semibold text-slate-200">
                        {result.tokens.token0}
                      </span>
                      <span className="text-slate-400">
                        {`${formatCurrency(result.initial.price0)} → ${formatCurrency(
                          result.current.price0
                        )}`}
                      </span>
                      <span
                        className={`text-xs font-semibold ${
                          (priceChanges?.token0 ?? 0) >= 0
                            ? "text-emerald-400"
                            : "text-rose-400"
                        }`}
                      >
                        {formatPercent(priceChanges?.token0 ?? 0)}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-3 justify-between items-center">
                      <span className="font-semibold text-slate-200">
                        {result.tokens.token1}
                      </span>
                      <span className="text-slate-400">
                        {`${formatCurrency(result.initial.price1)} → ${formatCurrency(
                          result.current.price1
                        )}`}
                      </span>
                      <span
                        className={`text-xs font-semibold ${
                          (priceChanges?.token1 ?? 0) >= 0
                            ? "text-emerald-400"
                            : "text-rose-400"
                        }`}
                      >
                        {formatPercent(priceChanges?.token1 ?? 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ILChecker;